import { differenceInMonths } from 'date-fns';
import { NATIONAL_AVERAGE_WAGE_INDEX_SERIES, BEND_POINTS } from './constants';

/*
 *
 *  The functions below calculate the estimated monthly social security benefits
 *  using the process described in https://www.ssa.gov/OACT/COLA/Benefits.html.
 *
 *
 *  Assumptions:
 *     - We don't know the person's actual historical earnings, so we calculate past
 *      earnings using the DEFAULT_WAGE_GROWTH_RATE.
 *     - We assume the person has worked continuously for the 35 years prior to retirement.
 *
 */

const ELIGIBILITY_AGE = 62;
const NORMAL_RETIREMENT_AGE = 67; // https://www.ssa.gov/OACT/ProgData/nra.html
const YEARS_OF_WORK = 35;
export const DEFAULT_WAGE_GROWTH_RATE = 0.05; // This is the assumed wage growth rate.

// Rounds a float to a specified number of decimal places (default to 2)
const roundFloat = (value: number, decimalPlaces?: number) => {
  return parseFloat(value.toFixed(decimalPlaces || 2));
};

/*
 *
 *   This function generates a lookup [year: factor] of Average
 *   Wage Index (AWI) index factors. These values are used to adjust
 *   a person's nominal earnings to indexed earnings for a target
 *   year.
 *
 *   See: https://www.ssa.gov/OACT/COLA/awifactors.html
 *
 */
export const generateIndexingFactors = (yearOfEligiblity: number) => {
  // Wage are indexed to the average wage level two years
  // prior to the year of first eligibility.
  // https://www.ssa.gov/OACT/COLA/awifactors.html
  const indexYear = yearOfEligiblity - 2;
  const indexWage = NATIONAL_AVERAGE_WAGE_INDEX_SERIES[indexYear];
  return Object.keys(NATIONAL_AVERAGE_WAGE_INDEX_SERIES).reduce((acc, year) => {
    if (Number(year) > indexYear) {
      return acc;
    }
    acc[year] = indexWage / NATIONAL_AVERAGE_WAGE_INDEX_SERIES[year];
    return acc;
  }, {});
};

/*
 *  This function calculates the estimated nominal earnings given a starting salary, the
 *  DEFAULT_WAGE_GROWTH_RATE defined above, and a starting year.
 *
 *  The benefit calculation uses the highest 35 of indexed earnings. Therefore, we calculate 35
 *  years of nominal earnings. And, since we are using a DEFAULT_WAGE_GROWTH_RATE, these will
 *  also be the highest years of income.
 *
 */
export const getEstimatedNominalEarnings = (startingSalary: number, startYear: number) => {
  return [...Array(YEARS_OF_WORK)].reduce(
    (acc, _, index) => {
      if (index === 0) {
        return acc;
      }
      const year = startYear - index;
      acc[year] = roundFloat(acc[year + 1] * (1 - DEFAULT_WAGE_GROWTH_RATE));
      return acc;
    },
    {
      [startYear]: startingSalary,
    }
  );
};

/*
 *  This function calculates the estimated nominal earnings given a starting salary, the
 *  DEFAULT_WAGE_GROWTH_RATE defined above, and a starting year.
 */
export const indexEarnings = (nominalEarnings, indexFactors) => {
  return Object.keys(nominalEarnings).reduce((acc, year) => {
    // Default to 1.0 if factor is missing as it's the current or future year.
    acc[year] = Math.trunc(nominalEarnings[year] as number) * (indexFactors[year] || 1);
    return acc;
  }, {});
};

/*
 *
 *  Calculate the Average Indexed Monthly Earnings (AIME)
 *
 */
export const calculateAIME = (earnings: number[]) => {
  return roundFloat(earnings.reduce((total, i) => total + i, 0) / earnings.length / 12);
};

/*
 *
 *   This function calculates the Primary Insurance amount based on
 *   the AIME and the retirement year.
 *
 *
 */
export const calculatePIA = (aime: number, year: number) => {
  const keys = Object.keys(BEND_POINTS);

  // Get bend point for year.
  // If we don't have a bend point for that year, use the most recent.
  const bendPoint = BEND_POINTS[year] || BEND_POINTS[keys[keys.length - 1]];
  const first = bendPoint.first;
  const second = bendPoint.second;

  let pia = 0;
  if (aime < bendPoint.first) {
    pia = 0.9 * aime;
  } else if (aime > first && aime <= second) {
    pia = 0.9 * first + 0.32 * (aime - first);
  } else {
    pia = 0.9 * first + 0.32 * (second - first) + 0.15 * (aime - second);
  }

  return Math.floor(pia * 10) / 10;
};

/*
 *   This function adjusts a person's monthly benefit (PIA) for early, late retirement.
 *
 *   See: https://www.ssa.gov/OACT/quickcalc/early_late.html
 *
 */
export const applyAdjustments = (pia: number, dob: Date, retirementAge: number) => {
  const eligiblityDate = new Date(dob);
  eligiblityDate.setFullYear(eligiblityDate.getFullYear() + ELIGIBILITY_AGE);

  const retirementDate = new Date(dob);
  retirementDate.setFullYear(retirementDate.getFullYear() + retirementAge);

  const fullRetirementDate = new Date(dob);
  fullRetirementDate.setFullYear(fullRetirementDate.getFullYear() + NORMAL_RETIREMENT_AGE);

  const monthsBeforeNormal = differenceInMonths(fullRetirementDate, retirementDate);

  // On or before normal retirement (less than PIA)
  if (monthsBeforeNormal >= 0) {
    return roundFloat(pia - monthsBeforeNormal * (5 / 9) - (monthsBeforeNormal % 60) * (5 / 12));
  }

  // Late retirement (more than PIA)
  return roundFloat(pia * (1 + (Math.abs(monthsBeforeNormal) / 12) * 0.08));
};

export default (currentSalary: number, dob: Date, retirementAge: number) => {
  const eligiblityYear = dob.getFullYear() + ELIGIBILITY_AGE;
  const indexFactors = generateIndexingFactors(eligiblityYear);
  const nominalEarnings = getEstimatedNominalEarnings(currentSalary, new Date().getFullYear());
  const indexedEarnings = indexEarnings(nominalEarnings, indexFactors);
  const aime = calculateAIME(Object.values(indexedEarnings));
  const pia = calculatePIA(aime, eligiblityYear);
  return applyAdjustments(pia, dob, retirementAge);
};
